import { useEffect } from 'react';

import type { MemberRecord } from './member-form-state';
import { memberDetailPath, type MembersListState } from './members-list-state';

export const useMembersKeyboard = ({
  enabled,
  rows,
  selectedId,
  onSelect,
  onOpenProfile,
  searchInputId,
}: {
  enabled: boolean;
  rows: MemberRecord[];
  selectedId: string | null;
  onSelect: (memberId: string | null) => void;
  onOpenProfile: (memberId: string) => void;
  searchInputId?: string;
}) => {
  useEffect(() => {
    if (!enabled) return;

    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName.toLowerCase();
      const isEditable =
        tag === 'input' ||
        tag === 'textarea' ||
        tag === 'select' ||
        target?.isContentEditable;

      if (event.key === '/' && !isEditable) {
        event.preventDefault();
        const input = searchInputId
          ? document.getElementById(searchInputId)
          : document.querySelector<HTMLInputElement>('input[aria-label]');
        input?.focus();
        return;
      }

      if (isEditable) return;

      const currentIndex = selectedId ? rows.findIndex((row) => row.id === selectedId) : -1;

      if (event.key === 'j' || event.key === 'ArrowDown') {
        event.preventDefault();
        const next = rows[currentIndex + 1] ?? rows[0];
        if (next) onSelect(next.id);
        return;
      }

      if (event.key === 'k' || event.key === 'ArrowUp') {
        event.preventDefault();
        const prev = currentIndex > 0 ? rows[currentIndex - 1] : rows[rows.length - 1];
        if (prev) onSelect(prev.id);
        return;
      }

      if (event.key === 'Enter' && selectedId) {
        event.preventDefault();
        onOpenProfile(selectedId);
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        onSelect(null);
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled, onOpenProfile, onSelect, rows, searchInputId, selectedId]);
};

export const buildProfileOpenHandler =
  (navigate: (path: string) => void, listState: MembersListState) =>
  (memberId: string) => {
    void navigate(memberDetailPath(memberId, listState));
  };
